import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Icon from "@mui/material/Icon";
import { useTranslation } from "react-i18next";

const LANGUAGE_STORAGE_KEY = "i18n_language";
const LANGUAGE_OPTIONS = [
  { code: "zh-TW", fallbackLabel: "繁體中文" },
  { code: "en", fallbackLabel: "English" },
  { code: "pt", fallbackLabel: "Português" },
  { code: "es", fallbackLabel: "Español" },
];

const normalizeLanguage = (code) => {
  if (!code) return null;
  const lowered = code.toLowerCase();
  if (lowered === "zh" || lowered === "zh-tw") return "zh-TW";
  if (lowered.startsWith("en")) return "en";
  if (lowered.startsWith("pt")) return "pt";
  if (lowered.startsWith("es")) return "es";
  return null;
};

const LanguageSwitcher = ({ iconColor = "inherit", size = "medium" }) => {
  const { i18n, t } = useTranslation("common");
  const [anchorEl, setAnchorEl] = useState(null);

  // Apply stored language on mount
  useEffect(() => {
    const storedLang = normalizeLanguage(localStorage.getItem(LANGUAGE_STORAGE_KEY));
    if (storedLang && storedLang !== i18n.language) {
      i18n.changeLanguage(storedLang);
    }
  }, [i18n]);

  const options = useMemo(
    () =>
      LANGUAGE_OPTIONS.map((option) => ({
        ...option,
        label: t(`languages.${option.code}`, option.fallbackLabel),
      })),
    [t]
  );

  const currentLanguage = i18n.resolvedLanguage || i18n.language || "en";
  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleSelect = (code) => {
    const normalized = normalizeLanguage(code) || "en";
    i18n.changeLanguage(normalized);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, normalized);
    handleClose();
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleOpen}
        aria-label="language switcher"
        size={size}
        sx={{ color: iconColor }}
      >
        <Icon>language</Icon>
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {options.map(({ code, label }) => (
          <MenuItem
            key={code}
            selected={code === currentLanguage}
            onClick={() => handleSelect(code)}
          >
            <ListItemIcon>
              <Icon fontSize="small">translate</Icon>
            </ListItemIcon>
            <ListItemText primary={label} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

LanguageSwitcher.propTypes = {
  iconColor: PropTypes.string,
  size: PropTypes.oneOf(["small", "medium", "large"]),
};

export default LanguageSwitcher;
